<template>
  <WdsAlert
    v-if="shownWarning"
    icon
    warning
    bordered
    class="tw-mb-4"
    :title="shownWarning.title"
    :description="shownWarning.description"
    data-test="rate-card-hire-warning"
    :data-comparison="shownWarning.comparison"
  />
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import { WdsAlert } from '@worksome/wds-components'
import { CustomFieldTypeValueInput, RateType } from '@/gql/graphql'
import { useHireRateCardBand } from '@/components/Hire/composables/useHireRateCardBand'
import { useHireRateCardBandGuidance } from '@/components/Hire/composables/useHireRateCardBandGuidance'

const props = defineProps<{
  jobId: string | null
  // Used when there is no job yet, or when the form changes the job's rate card criteria, rate
  // type or currency: the band then comes from the company and the form's custom field values.
  companyId?: string | null
  customFieldValues?: CustomFieldTypeValueInput[]
  formCriteriaChanged?: boolean
  rate: string | number
  rateType: string
  currencySymbol: string
  rateCardsEnabled: boolean
  // True only for an MSP (Company-as-supplier) offer — the fee-inclusive client leg is never
  // compared. A plain recruiter-submitted offer is still a direct hire and still gets the warning.
  rateSetByMsp: boolean
  // The rate was typed in the freelancer's currency, not the job's/card's — the backend can't
  // convert, so comparing the raw numbers would be comparing different currencies as if equal.
  preferFlCurrency: boolean
  // The currency the hire is posted in.
  rateCurrency: string
}>()

const rateType = computed(() => props.rateType.toUpperCase() as RateType)

const enabled = computed(
  () =>
    props.rateCardsEnabled &&
    !props.rateSetByMsp &&
    !props.preferFlCurrency &&
    (!!props.jobId || !!props.companyId) &&
    !!props.rateType,
)

const { band } = useHireRateCardBand({
  jobId: toRef(props, 'jobId'),
  companyId: computed(() => props.companyId ?? null),
  customFieldValues: computed(() => props.customFieldValues ?? []),
  rate: toRef(props, 'rate'),
  rateType,
  formCriteriaChanged: computed(() => props.formCriteriaChanged ?? false),
  enabled,
})

const { warning } = useHireRateCardBandGuidance({
  band,
  rate: toRef(props, 'rate'),
  currencySymbol: toRef(props, 'currencySymbol'),
})

// The band is saved in the job's currency. A hire in another currency is banded afresh on submit,
// so this band says nothing about it.
const shownWarning = computed(() =>
  band.value?.currency === props.rateCurrency.toUpperCase() ? warning.value : null,
)
</script>
